import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/context/language-context';
import { useAuth } from '@/context/auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AvailabilitySettings from './AvailabilitySettings';
import BookingsList from './BookingsList';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { Database } from '@/integrations/supabase/types';

// Définir le type pour une réservation
type Booking = Database['public']['Tables']['bookings']['Row'];

const EscortCalendar: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [date, setDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);

  // Choisir la locale en fonction de la langue actuelle
  const getLocale = () => {
    switch (currentLanguage) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  // Mettre useCallback sur loadBookings car utilisé dans useEffect
  // Déplacer la définition avant le useEffect
  const loadBookings = useCallback(async () => { 
    if (!user?.id) return; // Vérifier user.id ici
    setIsLoading(true);
    try {
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('escort_id', user.id) // Utiliser user.id directement
        .gte('booking_date', startOfMonth.toISOString())
        .lte('booking_date', endOfMonth.toISOString());
      
      if (error) throw error;
      
      setBookings(data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast({
        title: t('escort.calendar.error_loading_bookings'),
        description: t('escort.calendar.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  // Ajouter les dépendances de useCallback
  }, [date, supabase, t, toast, user?.id]);

  useEffect(() => {
    if (user) { // Garder la vérification de user ici pour le déclenchement initial
      loadBookings();
    }
    // Ajouter loadBookings aux dépendances
  }, [user, loadBookings]); // Retirer 'date' car il est déjà dans les dépendances de loadBookings

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">{t('escort.calendar.title')}</h1>
        <p className="text-muted-foreground">{t('escort.calendar.description')}</p>
      </div>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="grid grid-cols-2 mb-6">
          <TabsTrigger value="calendar">{t('escort.calendar.calendar_view')}</TabsTrigger>
          <TabsTrigger value="availability">{t('escort.calendar.availability_settings')}</TabsTrigger>
        </TabsList>
        
        <TabsContent value="calendar" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-4 col-span-1">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(date) => date && setDate(date)}
                className="rounded-md border w-full"
                locale={getLocale()}
              />
              
              <div className="mt-4">
                <h3 className="text-lg font-medium mb-2">
                  {format(date, 'PPP', { locale: getLocale() })}
                </h3>
                {isLoading ? (
                  <div className="flex items-center justify-center h-24">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <div>
                    {bookings.filter(booking => 
                      new Date(booking.booking_date).toDateString() === date.toDateString()
                    ).length > 0 ? (
                      <ul className="space-y-2">
                        {bookings
                          .filter(booking => new Date(booking.booking_date).toDateString() === date.toDateString())
                          .map(booking => (
                            <li key={booking.id} className="border rounded-md p-2">
                              <div className="font-medium">
                                {format(new Date(booking.booking_date), 'HH:mm')}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {booking.duration || t('escort.calendar.no_duration')}
                              </div>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        {t('escort.calendar.no_bookings_for_day')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-4 col-span-1 lg:col-span-2">
              <h2 className="text-xl font-semibold mb-4">{t('escort.calendar.upcoming_bookings')}</h2>
              <BookingsList escortId={user?.id} />
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="availability">
          <AvailabilitySettings escortId={user?.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EscortCalendar;
